import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, onSnapshot, writeBatch, serverTimestamp, doc, getDocs, collectionGroup, where, deleteField, updateDoc } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import { Teacher, isTeacher } from '../../../utils/firebase';
import { createItemNotification } from '../../../utils/notifications';
import { useLayoutContext } from '../../../components/layout/Layout';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';

const APPROVAL_TABLE_SCROLLBAR_CSS = `
  .item-approval-table-scrollbar::-webkit-scrollbar { width: 8px; }
  .item-approval-table-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .item-approval-table-scrollbar::-webkit-scrollbar-thumb { background: #D9D9D9; border-radius: 4px; }
  .item-approval-table-scrollbar::-webkit-scrollbar-thumb:hover { background: #C0C0C0; }
`;

// 아이템 사용 승인 요청 인터페이스
interface ItemApprovalRequest {
  id: string;
  studentId: string;
  studentName: string;
  itemId: string;
  itemName: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt?: Date;
  reason?: string;
  targetType: 'class' | 'subject';
  studentClassId: string;
  studentGrade: number;
  studentClass: number;
}

const ItemApproval: React.FC = () => {
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [approvalRequests, setApprovalRequests] = useState<ItemApprovalRequest[]>([]);
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ItemApprovalRequest | null>(null);
  const [openItemNotFoundDialog, setOpenItemNotFoundDialog] = useState(false);
  const [itemNotFoundRequest, setItemNotFoundRequest] = useState<ItemApprovalRequest | null>(null);
  const [openStudentItemsDialog, setOpenStudentItemsDialog] = useState(false);
  const [selectedStudentForItems, setSelectedStudentForItems] = useState<any>(null);
  const [studentItems, setStudentItems] = useState<any[]>([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [isBatchApproving, setIsBatchApproving] = useState(false);

  const pxToRem = (value: number) => `${value / 16}rem`;
  /**
   * 데스크톱: 뷰포트 기준 카드 높이 (네비·main 패딩·페이지 제목·요약 툴바·gap 대략 반영).
   * height/maxHeight 동값으로 지정해야 min-height:240만 적용되는 고정 박스가 되지 않음.
   */
  const ITEM_APPROVAL_CARD_HEIGHT_DESKTOP = 'calc(100dvh - 260px)';
  /** 목록·툴바 승인 버튼 — 행「승인」과 동일 (124×40, small 패딩 6px 12px) */
  const itemApprovalActionBtnWidth = pxToRem(124);
  const itemApprovalActionBtnHeight = pxToRem(40);
  /** 모바일 행 우측 승인 버튼 — 높이는 행(학생/아이템/날짜)에 맞춤 */
  const itemApprovalMobileActionBtnWidth = pxToRem(56);
  const allSelected =
    approvalRequests.length > 0 && selectedRequestIds.length === approvalRequests.length;
  const someSelected =
    selectedRequestIds.length > 0 && selectedRequestIds.length < approvalRequests.length;

  useEffect(() => {
    if (!currentUser || !isTeacher(currentUser)) {
      setLoading(false);
      return;
    }

    const teacher = currentUser as Teacher;
    if (!teacher.schoolId || !teacher.classId) {
      setLoading(false);
      setError('학급 정보를 불러올 수 없습니다. 담임 학급(classId)이 프로필에 설정되어 있는지 확인해 주세요.');
      return;
    }

    const subjectId = (teacher.subjectId || '').trim();

    // 학급 아이템 승인 요청 가져오기
    const classApprovalPath = `schools/${teacher.schoolId}/classes/${teacher.classId}/itemApprovals`;
    const classApprovalQuery = query(collection(db, classApprovalPath));

    // 학급 아이템 승인 요청 리스너
    const unsubscribeClassApprovals = onSnapshot(classApprovalQuery, (snapshot) => {
      const classRequests = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          targetType: 'class',
          studentClassId: doc.data().studentClassId || teacher.classId
        })) as ItemApprovalRequest[];
      
      // pending 상태인 요청만 필터링하고 생성일 기준으로 정렬
      const pendingClassRequests = classRequests
        .filter(req => req.status === 'pending')
        .sort((a, b) => {
          const aTime = a.createdAt?.getTime() || 0;
          const bTime = b.createdAt?.getTime() || 0;
          return bTime - aTime; // 최신순 정렬
        });
      
      setApprovalRequests(prev => {
        const subjectRequests = prev.filter(req => req.targetType === 'subject');
        return [...pendingClassRequests, ...subjectRequests];
      });
      setLoading(false);
    }, (error) => {
      console.error('학급 아이템 승인 요청 오류:', error);
      setError('학급 아이템 승인 요청을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    });

    // 교과 아이템 승인 요청 (교과 담당일 때만 — subjectId 없으면 잘못된 경로로 구독해 전체가 깨질 수 있음)
    let unsubscribeSubjectApprovals: (() => void) | undefined;
    if (subjectId) {
      unsubscribeSubjectApprovals = onSnapshot(
        query(
          collection(
            db,
            `schools/${teacher.schoolId}/subjects/${subjectId}/subjectitemApprovals`
          )
        ),
        (snapshot) => {
        const subjectRequests = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            targetType: 'subject',
            studentClassId: doc.data().studentClassId || teacher.classId
          })) as ItemApprovalRequest[];
        
        const pendingSubjectRequests = subjectRequests
          .filter(req => req.status === 'pending')
          .sort((a, b) => {
            const aTime = a.createdAt?.getTime() || 0;
            const bTime = b.createdAt?.getTime() || 0;
            return bTime - aTime;
          });
        
        setApprovalRequests(prev => {
          const classRequests = prev.filter(req => req.targetType === 'class');
          return [...classRequests, ...pendingSubjectRequests];
        });
      }, (error) => {
        console.error('교과 아이템 승인 요청 오류:', error);
        setError('교과 아이템 승인 요청을 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      });
    }

    return () => {
      unsubscribeClassApprovals();
      unsubscribeSubjectApprovals?.();
    };
  }, [currentUser]);

  // 학생 아이템 조회 함수
  const handleViewStudentItems = async (studentId: string, studentName: string) => {
    setSelectedStudentForItems({ id: studentId, name: studentName });
    setOpenStudentItemsDialog(true);

    try {
      if (!currentUser?.schoolId) return;

      // 학생 정보 조회
      const studentQuery = query(
        collectionGroup(db, 'students'),
        where('uid', '==', studentId),
        where('schoolId', '==', currentUser.schoolId)
      );
      
      const studentSnapshot = await getDocs(studentQuery);
      
      if (!studentSnapshot.empty) {
        const studentData = studentSnapshot.docs[0].data();
        const items = studentData.items || {};
        
        // items 객체를 배열로 변환 (used 상태 제외)
        const itemsArray = Object.entries(items)
          .map(([key, value]: [string, any]) => ({
            id: key,
            name: value.name || '알 수 없는 아이템',
            status: value.status || 'available',
            type: value.type || 'class',
            description: value.description || '',
            purchasedAt: value.purchasedAt?.toDate?.() || null
          }))
          .filter(item => item.status !== 'used');  // used 상태 제외
        
        setStudentItems(itemsArray);
      } else {
        setStudentItems([]);
      }
    } catch (err) {
      console.error('학생 아이템 조회 중 오류:', err);
      setError('학생 아이템을 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 체크박스 렌더링 함수
  const renderCheckbox = (checked: boolean, onChange: (checked: boolean) => void, indeterminate?: boolean) => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          onChange(!checked);
        }}
        style={{
          width: '20px',
          height: '20px',
          minWidth: '20px',
          minHeight: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          flexShrink: 0
        }}
      >
        {checked || indeterminate ? (
          // 체크된 상태 (오렌지색 원)
          <div
            style={{
              width: '20px',
              height: '20px',
              background: '#FF6F0F',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: 'none'
            }}
          >
            {/* 체크 마크 */}
            <svg width="12" height="12" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2.5L4 6.5L2 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ) : (
          // 체크 안된 상태 (흰색 배경, 회색 테두리)
          <div
            style={{
              width: '20px',
              height: '20px',
              background: 'white',
              border: '1.5px solid #D9D9D9',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          />
        )}
      </div>
    );
  };

  // 체크박스 선택/해제 핸들러
  const handleSelectRequest = (requestId: string, checked: boolean) => {
    if (checked) {
      setSelectedRequestIds(prev => [...prev, requestId]);
    } else {
      setSelectedRequestIds(prev => prev.filter(id => id !== requestId));
    }
  };

  // 전체 선택/해제 핸들러
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRequestIds(approvalRequests.map(req => req.id));
    } else {
      setSelectedRequestIds([]);
    }
  };

  // 일괄 승인 처리 (ids 생략 시 현재 체크 선택만)
  const handleBatchApproval = async (ids?: string[]) => {
    const targetIds = ids ?? selectedRequestIds;
    if (targetIds.length === 0) {
      setError('선택된 요청이 없습니다.');
      return;
    }

    if (!currentUser?.schoolId || !isTeacher(currentUser)) {
      console.error('교사 권한이 없거나 학교 ID가 없습니다.');
      return;
    }

    const teacher = currentUser as Teacher;
    setIsBatchApproving(true);
    setError(null);
    setSuccess(null);

    try {
      const selectedRequests = approvalRequests.filter(req => targetIds.includes(req.id));
      let successCount = 0;
      let failCount = 0;
      const failedRequests: string[] = [];

      // 각 요청을 순차적으로 처리
      for (const request of selectedRequests) {
        try {
          if (!request.studentClassId) {
            failCount++;
            failedRequests.push(request.studentName);
            continue;
          }

          const batch = writeBatch(db);
          
          // 승인 요청 상태 업데이트
          const requestPath = request.targetType === 'class' 
            ? `schools/${teacher.schoolId}/classes/${teacher.classId}/itemApprovals/${request.id}`
            : `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/subjectitemApprovals/${request.id}`;
          
          const requestRef = doc(db, requestPath);
          batch.update(requestRef, {
            status: 'approved',
            updatedAt: serverTimestamp(),
            reason: null,
            approvedBy: currentUser.uid,
            approvedAt: serverTimestamp()
          });

          // 학생 정보를 찾아서 아이템 상태 처리
          const studentQuery = query(
            collectionGroup(db, 'students'),
            where('uid', '==', request.studentId),
            where('schoolId', '==', teacher.schoolId)
          );
          
          const studentSnapshot = await getDocs(studentQuery);
          
          if (!studentSnapshot.empty) {
            const studentDoc = studentSnapshot.docs[0];
            const studentData = studentDoc.data();
            
            const studentItems = studentData.items || {};
            const allItems = Object.entries(studentItems) as [string, any][];
            const matching = allItems.filter(([key, value]) =>
              key === request.itemId
            );
            
            if (matching.length > 0) {
              const [useKey] = matching[0];
              
              // 승인된 경우: 아이템을 삭제하고 사용 권한 부여
              batch.update(studentDoc.ref, {
                [`items.${useKey}`]: deleteField()
              });
              
              // 아이템 사용 권한 부여
              const usagePath = request.targetType === 'class'
                ? `schools/${teacher.schoolId}/classes/${teacher.classId}/itemUsages/${request.id}`
                : `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/subjectitemUsages/${request.id}`;
              const itemUsageRef = doc(db, usagePath);
              batch.set(itemUsageRef, {
                studentId: request.studentId,
                itemId: request.itemId,
                approvalRequestId: request.id,
                approvedAt: serverTimestamp(),
                approvedBy: currentUser.uid,
                status: 'active'
              });

              await batch.commit();
              
              // 학생에게 결과 알림 전송
              try {
                await createItemNotification(
                  request.studentId,
                  '아이템 사용 승인 완료',
                  `${request.itemName} 아이템 사용이 승인되었습니다.`,
                  teacher.schoolId,
                  request.targetType === 'class' ? request.studentClassId : undefined,
                  'medium',
                  '/class/student/ClassMyItems',
                  {
                    itemName: request.itemName,
                    approvalRequestId: request.id,
                    result: 'approved'
                  }
                );
              } catch (notificationError) {
                console.error('학생 알림 전송 중 오류:', notificationError);
              }
              
              successCount++;
            } else {
              failCount++;
              failedRequests.push(request.studentName);
            }
          } else {
            failCount++;
            failedRequests.push(request.studentName);
          }
        } catch (err) {
          console.error(`요청 ${request.id} 처리 중 오류:`, err);
          failCount++;
          failedRequests.push(request.studentName);
        }
      }

      // 선택 초기화
      setSelectedRequestIds([]);

      // 결과 메시지 표시
      if (successCount > 0 && failCount === 0) {
        setSuccess(`${successCount}개의 요청이 승인되었습니다.`);
      } else if (successCount > 0 && failCount > 0) {
        setSuccess(`${successCount}개의 요청이 승인되었습니다.`);
        setError(`${failCount}개의 요청 처리에 실패했습니다: ${failedRequests.join(', ')}`);
      } else {
        setError(`모든 요청 처리에 실패했습니다: ${failedRequests.join(', ')}`);
      }
    } catch (err) {
      console.error('일괄 승인 처리 중 오류:', err);
      setError('일괄 승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsBatchApproving(false);
    }
  };

  // 아이템이 없는 승인 요청 삭제
  const handleDeleteItemNotFoundRequest = async () => {
    if (!itemNotFoundRequest || !currentUser?.schoolId) return;
    
    try {
      const teacher = currentUser as Teacher;
      const requestPath = itemNotFoundRequest.targetType === 'class' 
        ? `schools/${teacher.schoolId}/classes/${teacher.classId}/itemApprovals/${itemNotFoundRequest.id}`
        : `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/subjectitemApprovals/${itemNotFoundRequest.id}`;
      
      const requestRef = doc(db, requestPath);
      await updateDoc(requestRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
        reason: '아이템이 더 이상 존재하지 않음',
        cancelledBy: currentUser.uid,
        cancelledAt: serverTimestamp()
      });
      
      setSuccess('승인 요청이 삭제되었습니다.');
      
      // 승인 요청 목록에서 제거
      setApprovalRequests(prev => prev.filter(req => req.id !== itemNotFoundRequest.id));
      
    } catch (error) {
      console.error('승인 요청 삭제 오류:', error);
      setError('승인 요청 삭제 중 오류가 발생했습니다.');
    } finally {
      setOpenItemNotFoundDialog(false);
      setItemNotFoundRequest(null);
      setOpenApprovalDialog(false);
      setSelectedRequest(null);
    }
  };

  // 승인 요청 처리
  const handleApprovalRequest = async (request: ItemApprovalRequest, approved: boolean, reason?: string) => {
    if (!currentUser?.schoolId || !isTeacher(currentUser)) {
      console.error('교사 권한이 없거나 학교 ID가 없습니다.');
      return;
    }
    
    if (!request.studentClassId) {
      console.error('학생의 학급 정보가 없습니다.');
      setError('학생의 학급 정보가 없습니다.');
      return;
    }

    const teacher = currentUser as Teacher;

    try {
      const batch = writeBatch(db);
      
      // 승인 요청 상태 업데이트
      const requestPath = request.targetType === 'class' 
        ? `schools/${teacher.schoolId}/classes/${teacher.classId}/itemApprovals/${request.id}`
        : `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/subjectitemApprovals/${request.id}`;
      
      const requestRef = doc(db, requestPath);
      batch.update(requestRef, {
        status: approved ? 'approved' : 'rejected',
        updatedAt: serverTimestamp(),
        reason: reason || null,
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp()
      });

      // 학생 정보를 찾아서 아이템 상태 처리
      const studentQuery = query(
        collectionGroup(db, 'students'),
        where('uid', '==', request.studentId),
        where('schoolId', '==', teacher.schoolId)
      );
      
      const studentSnapshot = await getDocs(studentQuery);
      
      if (!studentSnapshot.empty) {
        const studentDoc = studentSnapshot.docs[0];
        const studentData = studentDoc.data();
        
        const studentItems = studentData.items || {};
        // 고유키별로 itemId가 일치하는 항목을 찾는다
        const allItems = Object.entries(studentItems) as [string, any][];
        const matching = allItems.filter(([key, value]) =>
          key === request.itemId  // uniqueKey와 직접 비교
        );
        
        if (matching.length === 0) {
          console.error('아이템 정보가 없습니다.');
          // 아이템이 없는 경우 다이얼로그 표시
          setItemNotFoundRequest(request);
          setOpenItemNotFoundDialog(true);
          return;
        } else {
          const [useKey] = matching[0];
        
          if (approved) {
            // 승인된 경우: 아이템을 삭제하고 사용 권한 부여
            batch.update(studentDoc.ref, {
              [`items.${useKey}`]: deleteField()
            });
            
            // 아이템 사용 권한 부여 (승인 요청 ID를 포함하여 고유성 보장)
            const usagePath = request.targetType === 'class'
              ? `schools/${teacher.schoolId}/classes/${teacher.classId}/itemUsages/${request.id}`
              : `schools/${teacher.schoolId}/subjects/${teacher.subjectId}/subjectitemUsages/${request.id}`;
            const itemUsageRef = doc(db, usagePath);
            batch.set(itemUsageRef, {
              studentId: request.studentId,
              itemId: request.itemId,
              approvalRequestId: request.id,
              approvedAt: serverTimestamp(),
              approvedBy: currentUser.uid,
              status: 'active'
            });
          } else {
            // 거절된 경우: 아이템 상태를 available로 되돌리기
            batch.update(studentDoc.ref, {
              [`items.${useKey}.status`]: 'available'
            });
          }
        }
      } else {
        console.error('학생 정보를 찾을 수 없습니다.');
        setError('학생 정보를 찾을 수 없습니다.');
        return;
      }

      await batch.commit();
      
      // 학생에게 결과 알림 전송
      try {
        const notificationMessage = approved
          ? `${request.itemName} 아이템 사용이 승인되었습니다.`
          : `${request.itemName} 아이템 사용이 거절되었습니다.${reason ? ` (사유: ${reason})` : ''}`;
        await createItemNotification(
          request.studentId,
          approved ? '아이템 사용 승인 완료' : '아이템 사용 거절 안내',
          notificationMessage,
          teacher.schoolId,
          request.targetType === 'class' ? request.studentClassId : undefined,
          'medium',
          '/class/student/ClassMyItems',
          {
            itemName: request.itemName,
            approvalRequestId: request.id,
            result: approved ? 'approved' : 'rejected'
          }
        );
      } catch (notificationError) {
        console.error('학생 알림 전송 중 오류:', notificationError);
      }

      setOpenApprovalDialog(false);
      setSuccess(approved ? '아이템 사용이 승인되었습니다.' : '아이템 사용이 거절되었습니다.');
    } catch (err) {
      console.error('승인 처리 중 오류:', err);
      setError('승인 처리 중 오류가 발생했습니다.');
    }
  };

  if (!currentUser || !isTeacher(currentUser)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        {loading ? (
          <div>로딩 중...</div>
        ) : (
          <div style={{ 
            color: 'red', 
            padding: '20px', 
            border: '1px solid red', 
            borderRadius: '4px' 
          }}>
            교사 권한이 필요합니다.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%',
      /* 모바일·데스크톱 모두: Layout main 높이가 auto일 때 flex:1 1 0 + minHeight:0 + overflow:hidden 체인이
         카드/목록 높이를 0으로 수축시켜 내용이 안 보임 — 콘텐츠 기준 높이로 페이지 스크롤 */
      height: 'auto',
      minHeight: 'fit-content',
      paddingLeft: '0',
      paddingRight: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? pxToRem(16) : pxToRem(32),
      overflow: 'visible',
      boxSizing: 'border-box',
    }}>
      {/* 에러/성공 메시지 */}
      {error && (
        <div style={{
          padding: isMobile ? '10px 16px' : '12px 16px',
          marginBottom: isMobile ? '12px' : '16px',
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          borderRadius: '8px',
          fontSize: isMobile ? '13px' : '14px',
          flexShrink: 0,
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          padding: isMobile ? '10px 16px' : '12px 16px',
          marginBottom: isMobile ? '12px' : '16px',
          backgroundColor: '#E8F5E9',
          color: '#2E7D32',
          borderRadius: '8px',
          fontSize: isMobile ? '13px' : '14px',
          flexShrink: 0,
        }}>
          {success}
        </div>
      )}

      <style>{APPROVAL_TABLE_SCROLLBAR_CSS}</style>

      {/* 페이지 제목 — 포인트 주간 승인과 동일 톤 */}
      <div
        style={{
          alignSelf: 'stretch',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--Color-text-normal, #161616)',
          fontSize: isMobile ? pxToRem(20) : pxToRem(32),
          fontFamily: 'Pretendard, sans-serif',
          fontWeight: '600',
          lineHeight: isMobile ? pxToRem(30) : pxToRem(48),
          padding: isMobile ? `${pxToRem(8)} 0 0` : '0',
          flexShrink: 0,
        }}
      >
        아이템 사용 승인
      </div>

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(12),
          flex: 'none',
          minHeight: 'fit-content',
        }}
      >
        {/* PointApprovalList와 동일: 상단 요약 + 일괄 액션 */}
        {!loading && (
          <div
            style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              justifyContent: 'flex-start',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: pxToRem(12),
              flexShrink: 0,
            }}
          >
            <div
              style={{
                flex: isMobile ? 'none' : '1 1 0',
                display: 'flex',
                alignItems: 'center',
                gap: pxToRem(8),
                minWidth: isMobile ? undefined : 0,
                flexWrap: 'nowrap',
              }}
            >
              {isMobile && approvalRequests.length > 0 && (
                <>
                  {renderCheckbox(allSelected, handleSelectAll, someSelected)}
                  <span
                    style={{
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: pxToRem(14),
                      fontFamily: 'Pretendard, sans-serif',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    전체
                  </span>
                </>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: pxToRem(4),
                  flexWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: isMobile ? pxToRem(16) : pxToRem(18),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: isMobile ? pxToRem(24) : pxToRem(27),
                  }}
                >
                  승인 대기
                </span>
                <span
                  style={{
                    color: 'var(--Color-fill-brand-color, #FF6F0F)',
                    fontSize: isMobile ? pxToRem(16) : pxToRem(18),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: isMobile ? pxToRem(24) : pxToRem(27),
                  }}
                >
                  {approvalRequests.length}
                </span>
                <span
                  style={{
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: isMobile ? pxToRem(16) : pxToRem(18),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: isMobile ? pxToRem(24) : pxToRem(27),
                  }}
                >
                  건
                </span>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                gap: pxToRem(8),
                width: isMobile ? '100%' : undefined,
                flexShrink: 0,
              }}
            >
              <div style={isMobile ? { flex: 1, minWidth: 0 } : undefined}>
                <Button
                  size="small"
                  width={isMobile ? '100%' : itemApprovalActionBtnWidth}
                  height={itemApprovalActionBtnHeight}
                  padding="6px 12px"
                  variant="submit"
                  onClick={() => handleBatchApproval()}
                  disabled={selectedRequestIds.length === 0 || isBatchApproving}
                >
                  {isBatchApproving ? '승인 중...' : '선택 일괄 승인'}
                </Button>
              </div>
              <div style={isMobile ? { flex: 1, minWidth: 0 } : undefined}>
                <Button
                  size="small"
                  width={isMobile ? '100%' : itemApprovalActionBtnWidth}
                  height={itemApprovalActionBtnHeight}
                  padding="6px 12px"
                  variant="submit"
                  onClick={() => handleBatchApproval(approvalRequests.map((r) => r.id))}
                  disabled={approvalRequests.length === 0 || isBatchApproving}
                >
                  {isBatchApproving ? '승인 중...' : '전체 승인'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ApprovalTable과 동일: 흰 카드 + 헤더 보더 + 스크롤 본문 */}
        <div
          style={{
            alignSelf: 'stretch',
            minHeight: isMobile ? 'auto' : 0,
            height: isMobile ? 'auto' : ITEM_APPROVAL_CARD_HEIGHT_DESKTOP,
            maxHeight: isMobile ? 'none' : ITEM_APPROVAL_CARD_HEIGHT_DESKTOP,
            paddingLeft: isMobile ? pxToRem(12) : pxToRem(20),
            paddingRight: isMobile ? pxToRem(12) : pxToRem(20),
            paddingTop: pxToRem(16),
            paddingBottom: pxToRem(16),
            background: 'var(--Color-fill-white, white)',
            borderRadius: pxToRem(16),
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 0,
            display: 'flex',
            overflow: isMobile ? 'visible' : 'hidden',
            flex: isMobile ? 'none' : '0 1 auto',
            boxSizing: 'border-box',
          }}
        >
          {!isMobile && (
            <div
              style={{
                alignSelf: 'stretch',
                borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                justifyContent: 'flex-start',
                alignItems: 'center',
                display: 'inline-flex',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: pxToRem(103),
                  paddingBottom: pxToRem(12),
                  paddingLeft: pxToRem(16),
                  paddingRight: pxToRem(16),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: pxToRem(12),
                  display: 'flex',
                }}
              >
                {renderCheckbox(allSelected, handleSelectAll, someSelected)}
                <div
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(13),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(19.5),
                  }}
                >
                  No
                </div>
              </div>
              <div
                style={{
                  width: pxToRem(120),
                  paddingBottom: pxToRem(12),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(13),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(19.5),
                  }}
                >
                  이름
                </div>
              </div>
              <div
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  paddingBottom: pxToRem(12),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(13),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(19.5),
                  }}
                >
                  아이템
                </div>
              </div>
              <div
                style={{
                  width: pxToRem(200),
                  paddingBottom: pxToRem(12),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(13),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(19.5),
                  }}
                >
                  요청일시
                </div>
              </div>
              <div
                style={{
                  width: pxToRem(124),
                  paddingBottom: pxToRem(12),
                  paddingLeft: pxToRem(8),
                  paddingRight: pxToRem(8),
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  display: 'flex',
                  marginLeft: 'auto',
                }}
              >
                <div
                  style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: pxToRem(13),
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '500',
                    lineHeight: pxToRem(19.5),
                  }}
                >
                  작업
                </div>
              </div>
            </div>
          )}

          <div
            className="item-approval-table-scrollbar"
            style={{
              alignSelf: 'stretch',
              flex: isMobile ? 'none' : '1 1 0',
              minHeight: isMobile ? 'auto' : 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: isMobile ? 'visible' : 'auto',
              overflowX: 'hidden',
              width: '100%',
            }}
          >
            {loading ? (
              <div style={{
                width: '100%',
                height: isMobile ? '200px' : '100px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: isMobile ? '13px' : '14px',
                fontFamily: 'Pretendard'
              }}>
                로딩 중...
              </div>
            ) : approvalRequests.length > 0 ? (
              <>
                {approvalRequests.map((request, index) => {
                // 모바일: 카드 형태, 데스크톱: 테이블 형태
                return isMobile ? (
                  <div key={request.id} style={{
                    width: '100%',
                    padding: `${pxToRem(12)} 0`,
                    borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: pxToRem(10),
                    boxSizing: 'border-box',
                  }}>
                    <div style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {renderCheckbox(
                        selectedRequestIds.includes(request.id),
                        (checked) => handleSelectRequest(request.id, checked)
                      )}
                    </div>

                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: '4px',
                    }}>
                      <div style={{
                        color: 'var(--Color-text-brand-color, #FF6F0F)',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => {
                        handleViewStudentItems(request.studentId, request.studentName);
                      }}
                      >
                        {request.studentName}
                      </div>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '14px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '21px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {request.itemName}
                      </div>
                      <div style={{
                        color: 'var(--Color-text-assistive, #989898)',
                        fontSize: '12px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '18px',
                      }}>
                        {request.createdAt.toISOString().slice(0, 16).replace('T', ' ').replace(/-/g, '.')}
                      </div>
                    </div>

                    <div style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignSelf: 'stretch',
                      alignItems: 'stretch',
                    }}>
                      <Button
                        variant="submit"
                        size="medium"
                        width={itemApprovalMobileActionBtnWidth}
                        height="100%"
                        padding="8px 10px"
                        onClick={() => {
                          setSelectedRequest(request);
                          setOpenApprovalDialog(true);
                        }}
                      >
                        승인
                      </Button>
                    </div>
                  </div>
                 ) : (
                   <div
                     key={request.id}
                     style={{
                       alignSelf: 'stretch',
                       borderBottom:
                         index < approvalRequests.length - 1
                           ? '1px solid var(--Color-line-normal, #F0F0F0)'
                           : 'none',
                       justifyContent: 'flex-start',
                       alignItems: 'center',
                       display: 'inline-flex',
                     }}
                   >
                     <div
                       style={{
                         width: pxToRem(103),
                         paddingLeft: pxToRem(16),
                         paddingRight: pxToRem(16),
                         paddingTop: pxToRem(12),
                         paddingBottom: pxToRem(12),
                         justifyContent: 'flex-start',
                         alignItems: 'center',
                         gap: pxToRem(12),
                         display: 'flex',
                       }}
                     >
                       {renderCheckbox(
                         selectedRequestIds.includes(request.id),
                         (checked) => handleSelectRequest(request.id, checked)
                       )}
                       <div
                         style={{
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: pxToRem(14),
                           fontFamily: 'Pretendard, sans-serif',
                           fontWeight: '400',
                           lineHeight: pxToRem(21),
                         }}
                       >
                         {String(index + 1).padStart(2, '0')}
                       </div>
                     </div>
                     <div
                       style={{
                         width: pxToRem(120),
                         paddingLeft: pxToRem(8),
                         paddingRight: pxToRem(8),
                         paddingTop: pxToRem(12),
                         paddingBottom: pxToRem(12),
                         justifyContent: 'flex-start',
                         alignItems: 'center',
                         display: 'flex',
                       }}
                     >
                       <button
                         type="button"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleViewStudentItems(request.studentId, request.studentName);
                         }}
                         style={{
                           color: 'var(--Color-text-brand-color, #FF6F0F)',
                           fontSize: pxToRem(14),
                           fontFamily: 'Pretendard, sans-serif',
                           fontWeight: '400',
                           lineHeight: pxToRem(21),
                           cursor: 'pointer',
                           border: 'none',
                           background: 'none',
                           padding: 0,
                           textAlign: 'left',
                           textDecoration: 'underline',
                           textDecorationColor: 'var(--Color-line-normal, #F0F0F0)',
                           textUnderlineOffset: pxToRem(2),
                         }}
                       >
                         {request.studentName}
                       </button>
                     </div>
                     <div
                       style={{
                         flex: '1 1 0',
                         minWidth: 0,
                         paddingLeft: pxToRem(8),
                         paddingRight: pxToRem(8),
                         paddingTop: pxToRem(12),
                         paddingBottom: pxToRem(12),
                         justifyContent: 'flex-start',
                         alignItems: 'center',
                         display: 'flex',
                       }}
                     >
                       <div
                         style={{
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: pxToRem(14),
                           fontFamily: 'Pretendard, sans-serif',
                           fontWeight: '400',
                           lineHeight: pxToRem(21),
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap',
                         }}
                       >
                         {request.itemName}
                       </div>
                     </div>
                     <div
                       style={{
                         width: pxToRem(200),
                         paddingLeft: pxToRem(8),
                         paddingRight: pxToRem(8),
                         paddingTop: pxToRem(12),
                         paddingBottom: pxToRem(12),
                         justifyContent: 'flex-start',
                         alignItems: 'center',
                         display: 'flex',
                       }}
                     >
                       <div
                         style={{
                           color: 'var(--Color-text-normal, #161616)',
                           fontSize: pxToRem(14),
                           fontFamily: 'Pretendard, sans-serif',
                           fontWeight: '400',
                           lineHeight: pxToRem(21),
                           whiteSpace: 'nowrap',
                         }}
                       >
                         {request.createdAt
                           .toISOString()
                           .slice(0, 16)
                           .replace('T', ' ')
                           .replace(/-/g, '.')}
                       </div>
                     </div>
                     <div
                       style={{
                         width: pxToRem(124),
                         paddingLeft: pxToRem(8),
                         paddingRight: pxToRem(8),
                         paddingTop: pxToRem(12),
                         paddingBottom: pxToRem(12),
                         justifyContent: 'flex-start',
                         alignItems: 'center',
                         display: 'flex',
                         marginLeft: 'auto',
                       }}
                     >
                       <Button
                         variant="submit"
                         size="small"
                         width={itemApprovalActionBtnWidth}
                         height={itemApprovalActionBtnHeight}
                         padding="6px 12px"
                         onClick={() => {
                           setSelectedRequest(request);
                           setOpenApprovalDialog(true);
                         }}
                       >
                         승인
                       </Button>
                     </div>
                   </div>
                 );
                })}
              </>
            ) : (
              <div
                style={{
                  alignSelf: 'stretch',
                  padding: pxToRem(24),
                  textAlign: 'center',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: pxToRem(14),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '400',
                  lineHeight: pxToRem(21),
                }}
              >
                승인 요청이 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 승인 요청 다이얼로그 */}
      {openApprovalDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? '12px' : '8px',
            maxWidth: isMobile ? 'calc(100% - 40px)' : '500px',
            width: isMobile ? 'calc(100% - 40px)' : '90%',
            maxHeight: isMobile ? 'calc(100vh - 40px)' : '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              padding: isMobile ? '16px' : '20px',
              borderBottom: '1px solid #e0e0e0',
              flexShrink: 0
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: isMobile ? '18px' : '1.25rem', 
                fontWeight: 'bold',
                fontFamily: 'Pretendard'
              }}>
                아이템 사용 승인 요청
              </h3>
            </div>
            <div style={{ 
              padding: isMobile ? '16px' : '20px', 
              flex: 1, 
              overflow: 'auto'
            }}>
              {selectedRequest && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                      학생: {selectedRequest.studentName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '500' }}>
                      아이템: {selectedRequest.itemName}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#666' }}>
                      아이템 타입: {selectedRequest.targetType === 'class' ? '학급 아이템' : '교과 아이템'}
                    </p>
                    <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: '#666' }}>
                      요청일시: {selectedRequest.createdAt.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '0.875rem', 
                      fontWeight: '500' 
                    }}>
                      거절 사유 (선택)
                    </label>
                    <textarea
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                      placeholder="거절 사유를 입력하세요..."
                      onChange={(e) => setSelectedRequest({
                        ...selectedRequest,
                        reason: e.target.value
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
            <div style={{ 
              padding: isMobile ? '16px' : '20px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: isMobile ? '8px' : '8px',
              flexDirection: isMobile ? 'column' : 'row', // 모바일: 세로 배치
              flexShrink: 0
            }}>
              <Button variant="cancel" width={isMobile ? '100%' : undefined} onClick={() => setOpenApprovalDialog(false)}>
                취소
              </Button>
              <Button
                variant="danger"
                width={isMobile ? '100%' : undefined}
                onClick={() => selectedRequest && handleApprovalRequest(selectedRequest, false, selectedRequest.reason)}
              >
                거절
              </Button>
              <Button
                variant="submit"
                width={isMobile ? '100%' : undefined}
                onClick={() => {
                  if (selectedRequest) {
                    handleApprovalRequest(selectedRequest, true);
                  }
                }}
              >
                승인
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 학생 아이템 조회 다이얼로그 */}
      {openStudentItemsDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <Card 
            width={isMobile ? "calc(100% - 40px)" : "600px"} 
            height={isMobile ? "calc(100vh - 40px)" : "500px"} 
            padding={isMobile ? "16px 20px" : "24px 28px"}
          >
            {/* 헤더 */}
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '600',
                color: '#161616',
                margin: 0,
                fontFamily: 'Pretendard'
              }}>
                {selectedStudentForItems?.name} 학생의 아이템
              </h2>
              <img 
                src="/images/icons/closeIcon.svg"
                alt="닫기"
                onClick={() => {
                  setOpenStudentItemsDialog(false);
                  setSelectedStudentForItems(null);
                  setStudentItems([]);
                }}
                style={{
                  width: isMobile ? '24px' : '32px',
                  height: isMobile ? '24px' : '32px',
                  padding: isMobile ? '6px' : '9.4px',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              />
            </div>

            {/* 아이템 목록 */}
            <div style={{
              width: '100%',
              height: isMobile ? 'calc(100% - 60px)' : '400px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '10px' : '12px',
              flex: 1
            }}>
              {studentItems.length > 0 ? (
                studentItems.map((item) => (
                  <div key={item.id} style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    borderRadius: '8px',
                    background: 'var(--Color-background-normal, #FFF)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#161616',
                        margin: 0
                      }}>
                        {item.name}
                      </h3>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: item.status === 'available' 
                          ? '#E8F5E9' 
                          : '#FFF3E0',
                        color: item.status === 'available' 
                          ? '#2E7D32' 
                          : '#EF6C00'
                      }}>
                        {item.status === 'available' ? '사용 가능' : '승인 대기'}
                      </div>
                    </div>
                    {item.description && (
                      <p style={{
                        fontSize: '13px',
                        color: '#4C4C4C',
                        margin: 0
                      }}>
                        {item.description}
                      </p>
                    )}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#989898'
                    }}>
                      <span>타입: {item.type === 'class' ? '학급 아이템' : '교과 아이템'}</span>
                      {item.purchasedAt && (
                        <span>구매일: {item.purchasedAt.toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#989898'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                  <p style={{ fontSize: '14px', margin: 0 }}>보유한 아이템이 없습니다.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* 아이템이 없는 승인 요청 삭제 확인 다이얼로그 */}
      {openItemNotFoundDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? '12px' : '12px',
            padding: isMobile ? '20px' : '24px',
            maxWidth: isMobile ? 'calc(100% - 40px)' : '400px',
            width: isMobile ? 'calc(100% - 40px)' : '90%',
            textAlign: 'center',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)'
          }}>
            <div style={{
              fontSize: isMobile ? '16px' : '18px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              color: '#161616',
              marginBottom: isMobile ? '12px' : '8px'
            }}>
              ⚠️ 아이템이 존재하지 않습니다
            </div>
            <div style={{
              fontSize: isMobile ? '13px' : '14px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              color: '#4C4C4C',
              marginBottom: isMobile ? '20px' : '20px',
              lineHeight: isMobile ? '20px' : '20px'
            }}>
              <strong>{itemNotFoundRequest?.studentName}</strong> 학생의<br/>
              <strong>"{itemNotFoundRequest?.itemName}"</strong> 아이템이<br/>
              더 이상 존재하지 않습니다.<br/><br/>
              승인 요청을 삭제하시겠습니까?
            </div>
            <div style={{
              display: 'flex',
              gap: isMobile ? '8px' : '12px',
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row', // 모바일: 세로 배치
            }}>
              <Button
                variant="line"
                width={isMobile ? '100%' : undefined}
                onClick={() => {
                  setOpenItemNotFoundDialog(false);
                  setItemNotFoundRequest(null);
                  setOpenApprovalDialog(false);
                  setSelectedRequest(null);
                }}
              >
                취소
              </Button>
              <Button
                variant="primary"
                width={isMobile ? '100%' : undefined}
                onClick={handleDeleteItemNotFoundRequest}
              >
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemApproval;

